import {
  Box,
  Flex,
  IconButton,
  Spinner,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import {
  Cell,
  ColumnDef,
  ColumnSizingState,
  Header,
  Row,
  Table,
  flexRender,
} from '@tanstack/react-table'
import { VirtualItem } from '@tanstack/react-virtual'
import React from 'react'

import { Icon } from '../Icons/Icon'
import { ColumnMenu } from './ColumnMenu'
import { ContextMenu } from './ContextMenu'

export interface ContextMenuState {
  isOpen: boolean
  x: number
  y: number
  cellText: string
  rowText: string
}

export interface ColumnMenuState<TData extends Record<string, any>> {
  isOpen: boolean
  x: number
  y: number
  columnId: string | null
}

interface CalculateAutoColumnSizingParams<TData extends Record<string, any>> {
  columns: ColumnDef<TData, any>[]
  data: TData[]
  defaultMaxAutoWidth: number
  useData: boolean
  columnIds?: string[]
}

interface ExpandColumnSizingToFillWidthParams {
  sizing: ColumnSizingState
  columnIds: string[]
  containerWidth: number
}

interface DataTableHeaderCellProps<TData extends Record<string, any>> {
  header: Header<TData, unknown>
  index: number
  columnCount: number
  onOpenColumnMenu: (
    event: React.MouseEvent<HTMLDivElement>,
    column: Header<TData, unknown>['column'],
  ) => void
}

interface DataTableBodyProps<TData extends Record<string, any>> {
  totalSize: number
}

interface DataTableLoadingRowProps<TData extends Record<string, any>> {
  table: Table<TData>
}

interface DataTableBodyRowProps<TData extends Record<string, any>> {
  row: Row<TData>
  virtualRow: VirtualItem
  measureElement: (element: HTMLElement | null) => void
  onRowClick?: (event: React.MouseEvent, row: Row<TData>) => void
}

interface DataTableBodyCellProps<TData extends Record<string, any>> {
  cell: Cell<TData, unknown>
  onContextMenu: (event: React.MouseEvent, cell: Cell<TData, unknown>) => void
}

interface DataTableWrapperProps {
  minHeight?: string | number
  height?: string | number
}

interface DataTableMenusProps<TData extends Record<string, any>> {
  contextMenu: ContextMenuState
  onCloseContextMenu: () => void
  onCopyCell: () => void
  onCopyRow: () => void
  columnMenu: ColumnMenuState<TData>
  onCloseColumnMenu: () => void
  onAutosizeColumn: (columnId: string) => void
  onAutosizeAllColumns: () => void
  onResetColumns: () => void
  table: Table<TData>
}

export const DEFAULT_PAGINATION = {
  pageIndex: 0,
  pageSize: 150,
}

export const DEFAULT_CONTEXT_MENU_STATE: ContextMenuState = {
  isOpen: false,
  x: 0,
  y: 0,
  cellText: '',
  rowText: '',
}

export const createDefaultColumnMenuState = <
  TData extends Record<string, any>,
>(): ColumnMenuState<TData> => ({
  isOpen: false,
  x: 0,
  y: 0,
  columnId: null,
})

export const DataTableWrapper = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<DataTableWrapperProps>
>(({ minHeight, height, children }, ref) => {
  return (
    <Box
      ref={ref}
      overflow="auto"
      position="relative"
      minHeight={minHeight}
      height={height}
      width="full"
      borderRadius="8px"
      border="1px solid"
      borderColor="presets.neutral.20"
      bg="white"
    >
      {children}
    </Box>
  )
})

const getColumnId = <TData extends Record<string, any>>(
  column: ColumnDef<TData, any>,
) => {
  if ('id' in column && column.id) {
    return column.id
  }

  if ('accessorKey' in column && typeof column.accessorKey === 'string') {
    return column.accessorKey
  }

  return undefined
}

const getColumnHeaderText = <TData extends Record<string, any>>(
  column: ColumnDef<TData, any>,
  fallback: string,
) => {
  if ('header' in column && typeof column.header === 'string') {
    return column.header
  }

  return fallback
}

const getColumnValue = <TData extends Record<string, any>>(
  column: ColumnDef<TData, any>,
  row: TData,
) => {
  if ('accessorFn' in column && typeof column.accessorFn === 'function') {
    return column.accessorFn(row, 0)
  }

  if ('accessorKey' in column && typeof column.accessorKey === 'string') {
    return row[column.accessorKey]
  }

  return undefined
}

const measureTextWidth = (
  text: string,
  canvasContext: CanvasRenderingContext2D | null,
  padding: number,
) => {
  if (canvasContext) {
    return canvasContext.measureText(text).width + padding
  }

  return text.length * 8 + padding
}

export const calculateAutoColumnSizing = <TData extends Record<string, any>>({
  columns,
  data,
  defaultMaxAutoWidth,
  useData,
  columnIds,
}: CalculateAutoColumnSizingParams<TData>) => {
  const canvas = document.createElement('canvas')
  const canvasContext = canvas.getContext('2d')
  const sizing: ColumnSizingState = {}
  const targetColumnIds = columnIds ? new Set(columnIds) : undefined

  columns.forEach(column => {
    const columnId = getColumnId(column)

    if (!columnId || (targetColumnIds && !targetColumnIds.has(columnId))) {
      return
    }

    if (canvasContext) {
      canvasContext.font = '600 12px Montserrat'
    }

    let maxWidth = measureTextWidth(
      getColumnHeaderText(column, columnId),
      canvasContext,
      72,
    )

    if (useData && data.length > 0) {
      if (canvasContext) {
        canvasContext.font = '500 12px Montserrat'
      }

      const rowsToMeasure = data.slice(0, 500)

      for (const row of rowsToMeasure) {
        const value = getColumnValue(column, row)
        const text =
          column.meta?.toText?.(value, row as TData) ?? String(value ?? '')

        if (!text) {
          continue
        }

        maxWidth = Math.max(maxWidth, measureTextWidth(text, canvasContext, 32))
      }
    }

    sizing[columnId] = Math.min(
      maxWidth,
      column.meta?.maxAutoWidth ?? defaultMaxAutoWidth,
    )
  })

  return sizing
}

export const expandColumnSizingToFillWidth = ({
  sizing,
  columnIds,
  containerWidth,
}: ExpandColumnSizingToFillWidthParams) => {
  if (!columnIds.length || containerWidth <= 0) {
    return sizing
  }

  const totalWidth = columnIds.reduce(
    (sum, columnId) => sum + (sizing[columnId] ?? 0),
    0,
  )

  if (totalWidth >= containerWidth) {
    return sizing
  }

  const expandedSizing = { ...sizing }
  const remainingWidth = containerWidth - totalWidth
  const baseWidth = totalWidth || columnIds.length
  let distributedWidth = 0

  columnIds.forEach((columnId, index) => {
    const currentWidth = expandedSizing[columnId] ?? 0
    const widthShare =
      index === columnIds.length - 1
        ? remainingWidth - distributedWidth
        : Math.round(
            remainingWidth * ((totalWidth ? currentWidth : 1) / baseWidth),
          )

    expandedSizing[columnId] = currentWidth + widthShare
    distributedWidth += widthShare
  })

  return expandedSizing
}

export const getCellText = <TData extends Record<string, any>>(
  cell: Cell<TData, unknown>,
) => {
  const meta = cell.column.columnDef.meta

  if (meta?.toText) {
    return meta.toText(cell.getValue(), cell.row.original)
  }

  return String(cell.getValue() ?? '')
}

export const getRowText = <TData extends Record<string, any>>(
  row: Row<TData>,
) => {
  return row
    .getVisibleCells()
    .map(cell => {
      const header = cell.column.columnDef.header
      const headerText = typeof header === 'string' ? header : cell.column.id

      return `${headerText}: ${getCellText(cell)}`
    })
    .join('\t')
}

export const createContextMenuState = <TData extends Record<string, any>>(
  event: React.MouseEvent,
  cell: Cell<TData, unknown>,
): ContextMenuState => ({
  isOpen: true,
  x: event.clientX,
  y: event.clientY,
  cellText: getCellText(cell),
  rowText: getRowText(cell.row),
})

const getPinnedBoundaryStyles = <
  TData extends Record<string, any>,
  TColumn extends { getIsPinned: () => false | 'left' | 'right' },
>(
  column: TColumn & {
    getIsLastColumn: (position: 'left' | 'right') => boolean
    getIsFirstColumn: (position: 'left' | 'right') => boolean
  },
) => {
  const isPinned = column.getIsPinned()
  const isLastLeftPinned = isPinned === 'left' && column.getIsLastColumn('left')
  const isFirstRightPinned =
    isPinned === 'right' && column.getIsFirstColumn('right')

  return {
    isPinned,
    borderRight: isLastLeftPinned
      ? '1px solid var(--chakra-colors-presets-neutral-20)'
      : 'none',
    borderLeft: isFirstRightPinned
      ? '1px solid var(--chakra-colors-presets-neutral-20)'
      : 'none',
  }
}

const getPinnedPositionStyles = <
  TData extends Record<string, any>,
  TColumn extends {
    getIsPinned: () => false | 'left' | 'right'
    getStart: (position: 'left' | 'right') => number
    getAfter: (position: 'left' | 'right') => number
  },
>(
  column: TColumn,
) => {
  const isPinned = column.getIsPinned()

  return {
    position: isPinned ? 'sticky' : 'relative',
    left: isPinned === 'left' ? column.getStart('left') : undefined,
    right: isPinned === 'right' ? column.getAfter('right') : undefined,
    zIndex: isPinned ? 3 : 1,
  } as const
}

const getHeaderCellStyle = <TData extends Record<string, any>>(
  header: Header<TData, unknown>,
  index: number,
  columnCount: number,
) => {
  const pinnedBoundaryStyles = getPinnedBoundaryStyles(header.column)
  const pinnedPositionStyles = getPinnedPositionStyles(header.column)

  return {
    display: 'flex',
    width: header.getSize(),
    ...pinnedPositionStyles,
    alignItems: 'center',
    height: '42px',
    padding: '8px 16px',
    borderBottom: '1px solid var(--chakra-colors-presets-neutral-20)',
    borderRight: pinnedBoundaryStyles.borderRight,
    borderLeft: pinnedBoundaryStyles.borderLeft,
    borderTopLeftRadius: index === 0 ? '8px' : '0',
    borderTopRightRadius: index === columnCount - 1 ? '8px' : '0',
    backgroundColor: 'var(--chakra-colors-presets-neutral-10)',
    color: 'var(--chakra-colors-presets-turquoise-100)',
    fontFamily: 'Montserrat',
    fontWeight: 600,
    fontSize: '12px',
    letterSpacing: '0.2px',
    textTransform: 'none',
  } as const
}

const getBodyCellStyle = <TData extends Record<string, any>>(
  cell: Cell<TData, unknown>,
) => {
  const pinnedBoundaryStyles = getPinnedBoundaryStyles(cell.column)
  const pinnedPositionStyles = getPinnedPositionStyles(cell.column)

  return {
    display: 'flex',
    width: cell.column.getSize(),
    ...pinnedPositionStyles,
    zIndex: pinnedBoundaryStyles.isPinned ? 2 : 1,
    backgroundColor: pinnedBoundaryStyles.isPinned ? 'inherit' : 'transparent',
    alignItems: 'center',
    padding: '8px 16px',
    height: '44px',
    borderBottom: '1px solid var(--chakra-colors-presets-neutral-20)',
    borderRight: pinnedBoundaryStyles.borderRight,
    borderLeft: pinnedBoundaryStyles.borderLeft,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as const
}

export const DataTableHeader = ({ children }: React.PropsWithChildren) => {
  return (
    <Thead
      style={{
        display: 'grid',
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: 'var(--chakra-colors-presets-neutral-10)',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
      }}
    >
      {children}
    </Thead>
  )
}

export const DataTableHeaderGroupRow = ({
  children,
}: React.PropsWithChildren) => {
  return <Tr style={{ display: 'flex', width: '100%' }}>{children}</Tr>
}

export const DataTableHeaderCell = <TData extends Record<string, any>>({
  header,
  index,
  columnCount,
  onOpenColumnMenu,
}: DataTableHeaderCellProps<TData>) => {
  return (
    <Th
      style={getHeaderCellStyle(header, index, columnCount)}
      onClick={header.column.getToggleSortingHandler()}
      cursor={header.column.getCanSort() ? 'pointer' : 'default'}
      _hover={{
        backgroundColor: 'var(--chakra-colors-presets-neutral-20)',
      }}
      role="group"
    >
      <Box
        flex="1"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}
      </Box>

      {header.column.getIsSorted() && (
        <Box ml={2} display="flex" alignItems="center">
          <Icon
            icon={
              header.column.getIsSorted() === 'asc'
                ? 'ArrowUpIcon'
                : 'ArrowDownIcon'
            }
            boxSize="16px"
            color="presets.turquoise.100"
          />
        </Box>
      )}

      <Box
        className="column-menu-btn"
        opacity={0}
        _groupHover={{ opacity: 1 }}
        transition="opacity 0.2s"
        ml={2}
        onClick={event => {
          event.stopPropagation()
          onOpenColumnMenu(event, header.column)
        }}
      >
        <IconButton
          aria-label="Column Menu"
          icon={<Icon icon="EllipsisMenuIcon" boxSize="16px" />}
          size="xs"
          variant="ghost"
          color="presets.turquoise.100"
          _hover={{ bg: 'presets.neutral.30' }}
        />
      </Box>

      {header.column.getCanResize() && (
        <Box
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          onClick={event => event.stopPropagation()}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            height: '100%',
            width: '5px',
            background: header.column.getIsResizing()
              ? 'var(--chakra-colors-presets-turquoise-50)'
              : 'transparent',
            cursor: 'col-resize',
            userSelect: 'none',
            touchAction: 'none',
          }}
          _hover={{
            background: 'var(--chakra-colors-presets-turquoise-50)',
          }}
        />
      )}
    </Th>
  )
}

export const DataTableBody = ({
  totalSize,
  children,
}: React.PropsWithChildren<DataTableBodyProps<Record<string, any>>>) => {
  return (
    <Tbody
      style={{
        display: 'grid',
        height: `${totalSize}px`,
        position: 'relative',
      }}
    >
      {children}
    </Tbody>
  )
}

export const DataTableLoadingRow = <TData extends Record<string, any>>({
  table,
}: DataTableLoadingRowProps<TData>) => {
  return (
    <Tr
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        height: '200px',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Td
        colSpan={table.getHeaderGroups()[0]?.headers.length || 1}
        borderBottom="none"
        style={{ display: 'flex', justifyContent: 'center' }}
      >
        <Flex
          justify="center"
          align="center"
          bg="whiteAlpha.80"
          w="full"
          h="full"
        >
          <Spinner />
        </Flex>
      </Td>
    </Tr>
  )
}

export const DataTableBodyRow = <TData extends Record<string, any>>({
  row,
  virtualRow,
  measureElement,
  onRowClick,
  children,
}: React.PropsWithChildren<DataTableBodyRowProps<TData>>) => {
  const isSelected = row.getIsSelected()

  return (
    <Tr
      data-index={virtualRow.index}
      ref={measureElement}
      onClick={e => {
        if (onRowClick) {
          onRowClick(e, row)
        } else {
          row.toggleSelected()
        }
      }}
      style={{
        display: 'flex',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        transform: `translateY(${virtualRow.start}px)`,
        backgroundColor: isSelected
          ? 'var(--chakra-colors-presets-turquoise-10)'
          : 'white',
        cursor: 'default',
        fontFamily: 'Montserrat',
        fontWeight: 600,
        fontSize: '12px',
        letterSpacing: '0.2px',
        color: 'var(--chakra-colors-presets-neutral-90)',
      }}
      _hover={{
        backgroundColor: 'var(--chakra-colors-presets-turquoise-10)',
      }}
      role="group"
    >
      {children}
    </Tr>
  )
}

export const DataTableBodyCell = <TData extends Record<string, any>>({
  cell,
  onContextMenu,
}: DataTableBodyCellProps<TData>) => {
  return (
    <Td
      onContextMenu={event => onContextMenu(event, cell)}
      style={getBodyCellStyle(cell)}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </Td>
  )
}

export const DataTableMenus = <TData extends Record<string, any>>({
  contextMenu,
  onCloseContextMenu,
  onCopyCell,
  onCopyRow,
  columnMenu,
  onCloseColumnMenu,
  onAutosizeColumn,
  onAutosizeAllColumns,
  onResetColumns,
  table,
}: DataTableMenusProps<TData>) => {
  return (
    <>
      <ContextMenu
        isOpen={contextMenu.isOpen}
        x={contextMenu.x}
        y={contextMenu.y}
        onClose={onCloseContextMenu}
        onCopyCell={onCopyCell}
        onCopyRow={onCopyRow}
      />

      {columnMenu.columnId
        ? (() => {
            const currentColumn = table.getColumn(columnMenu.columnId)

            if (!currentColumn) {
              return null
            }

            return (
              <ColumnMenu
                isOpen={columnMenu.isOpen}
                x={columnMenu.x}
                y={columnMenu.y}
                onClose={onCloseColumnMenu}
                onAutosizeColumn={onAutosizeColumn}
                onAutosizeAllColumns={onAutosizeAllColumns}
                onResetColumns={onResetColumns}
                column={currentColumn}
                table={table}
              />
            )
          })()
        : null}
    </>
  )
}
